import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, forwardRef, useContext } from 'react';
/**
 * FieldContext — propagated by <Field> so its child <Input> /
 * <Textarea> / <Select> can pick up the field's auto-generated
 * id without the consumer wiring `id` by hand. Standalone usage
 * is still fine — the context value defaults to an empty string
 * and the consumer can pass `id` explicitly.
 */
const FieldContext = createContext('');
export const FieldContextProvider = FieldContext.Provider;
export function useFieldId() {
    return useContext(FieldContext);
}
/**
 * Input — styled `<input>` that pulls colors/spacing from design
 * tokens. Wraps the native element so all standard props (value,
 * onChange, onBlur, autoComplete, etc.) work as expected.
 *
 * If the input is rendered inside a `<Field>`, the field's
 * generated id is auto-applied as the input's `id` (so the field's
 * `<label htmlFor>` and the input's `id` match). Standalone
 * usage is also fine — pass `id` explicitly to override.
 *
 * @example
 *   <Field label="Email">
 *     <Input type="email" name="email" autoComplete="email" required />
 *   </Field>
 */
export const Input = forwardRef(function Input({ type = 'text', className, id, ...rest }, ref) {
    const fieldId = useFieldId();
    const resolvedId = id ?? fieldId;
    const classes = ['osd-input', className].filter(Boolean).join(' ');
    return _jsx("input", { ref: ref, id: resolvedId, type: type, className: classes, ...rest });
});
//# sourceMappingURL=Input.js.map